import type { Subscription } from '~/types/user'

export interface Group {
  id: string
  owner_id: string
  name: string
  access_type: number
  verification_level: number
  created_at?: string
  avatar_id: string
  banner_id: string
  avatar_url: string
  banner_url: string
  /** Personal (per-user) push-notification mute for this group - set via setGroupMute. */
  is_muted: boolean
}

export enum ChannelType {
  Text,
  Voice,
  DirectMessage,
  GroupMessage,
  Tribune,
  Posts,
  Forum,
  Events,
  /** Kanban board backed by an external tracker (YouTrack first) — see app/components/voice-ui/Boards. */
  Board,
}

export interface Channel {
  id: string
  name: string
  type: ChannelType
  position: string
  description?: string
  users: GroupUser[]
  category_id: string
  user_limit?: number
  /** Mic audio bitrate in kbps (8-128), server-validated. */
  bitrate?: number
  /**
   * If true (default for new channels), the channel inherits its category's permission
   * overwrites and its own overwrites (set via setChannelPermissionOverwrite) are ignored
   * entirely when resolving effective permissions. If false, the channel lives by its own
   * overwrites and the category has no effect on it. Toggle via setChannelCategorySync.
   */
  is_category_permissions_sync: boolean
}

export interface GroupUser {
  id: string
  username: string
  email: string
  display_name: string
  description: string
  // Not sent by the backend yet — the profile shows a "Биография" block once it is.
  biography?: string
  status: string
  preferred_language: string
  created_at?: string
  updated_at?: string
  last_activity?: string
  is_confirmed: boolean
  is_bot: boolean
  subscription: Subscription | null
  avatar_url: string
  banner_url: string
  user_status: number
  role_ids: string[]
  // Only meaningful when this `GroupUser` came from `Channel.users` (backed by
  // `common.ChannelVoiceUser` — see `ChannelHiddenParticipantService` on the backend). Always
  // `false` on group roster/member entries, which don't carry live voice state.
  mic_is_enabled?: boolean
  cam_is_enabled?: boolean
  is_deafened?: boolean
}

// A user currently banned from a group — returned by GetGroupBans (moderation-only visibility,
// requires BanMembers). The banned user is no longer a member, so this is kept separate from
// `GroupUser` rather than reusing it.
export interface BannedGroupUser {
  user: GroupUser
  reason: string
  banned_by_user_id: string
  banned_at?: string
}

export interface Category {
  id: string
  name: string
  position: string
  channels: Channel[]
}

export interface GroupDetails {
  id: string
  owner_id: string
  name: string
  access_type: number
  verification_level: number
  created_at?: string
  categories: Category[]
  avatar_id: string
  banner_id: string
  avatar_url: string
  banner_url: string
  i_am_is_owner: boolean
  /** Admin-set default `NotificationLevel` (0 = not set yet, treated as ALL_MESSAGES). */
  default_notification_level?: number
}

export interface Role {
  id: string
  group_id: string
  name: string
  position: string
  color: string
  permissions: number[]
  /** Show the role's members separately from the other online members in the member list. */
  hoist: boolean
  /** Anyone can mention the role as `<@&id>`; otherwise only members with MentionEveryone. */
  mentionable?: boolean
}

// A channel-level override of a role's (or member's) server-wide permissions — Discord-style
// per-channel "allow"/"deny" bitmasks; anything not covered by either mask inherits the role's
// normal permission. `allow`/`deny` are already combined bitmasks (unlike Role.permissions, which
// the server sends as separate int64 chunks — see permissionsToMask in lib/permissions.ts).
export interface ChannelPermissionOverwrite {
  target_id: string
  is_role_target: boolean
  allow: number
  deny: number
}

// Same shape as ChannelPermissionOverwrite, just scoped to a category instead of a channel — kept
// as a distinct type (matching the distinct `CategoryPermissionOverwrite` proto message) rather
// than reusing ChannelPermissionOverwrite, since the two are resolved independently server-side.
export interface CategoryPermissionOverwrite {
  target_id: string
  is_role_target: boolean
  allow: number
  deny: number
}

export interface Invite {
  id: string
  group_id: string
  owner_id: string
  invite_code: string
  created_at?: string
  expires_at?: string
  uses_amount: number
  max_use_amount: number
}
